<template>
    <div class="media-download-dialog shadow-lg card" v-if="shouldShowDownloadPanel">
        <div class="d-flex align-items-center card-header">
            {{ $t('site.downloads') }}

            <button class="btn btn-primary btn-sm ms-auto" @click="clearDownloads()">
                <Close></Close>
            </button>
        </div>
        <div class="card-body">
            <div v-for="(item, index) of items" :key="item.id" class="d-flex py-2 align-items-center">
                {{ $t('site.download') }} {{ index + 1 }}

                <div class="ms-auto">
                    <div v-if="item.status === 'pending' || (item.status === 'working' && item.progress === null) || (item.status === 'working' && item.progress as number >= 100)" class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <div v-else-if="item.status === 'working'" style="min-width: 150px" class="progress" role="progressbar" aria-label="Basic example" :aria-valuenow="item.progress as number" aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar" :style="`width: ${item.progress}%`"></div>
                    </div>
                    <Check v-else-if="item.status === 'finished'"></Check>
                    <Alert v-else-if="item.status === 'failed'"></Alert>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, reactive, ref, watch} from "vue";
import {findIndex} from "lodash";
import Check from "@/site/components/Icons/Check.vue";
import Alert from "@/site/components/Icons/Alert.vue";
import Close from "@/site/components/Icons/Close.vue";

export interface Item {
    id: number,
    progress: number | null,
    status: 'pending' | 'working' | 'finished' | 'failed',
}

const props = withDefaults(defineProps<{
    downloads: number[]
}>(), {
    downloads: () => [],
});

const registeredItems = ref<number[]>([]);
const items = reactive<Item[]>([]);

const shouldShowDownloadPanel = computed(() => {
    return items.filter(item => {
        return ['pending', 'working', 'failed'].includes(item.status);
    }).length > 0;
});

watch(() => props.downloads, () => {
    for (const download of props.downloads) {
        if (registeredItems.value.includes(download)) {
            continue;
        }

        items.push({
            id: download,
            progress: null,
            status: 'pending'
        } as Item);

        registeredItems.value.push(download);

        startListening(download);
    }
}, {deep: true});

const clearDownloads = () => {
    for (const item of items) {
        window.Echo.leave(`media_downloads.${item.id}`);
    }

    items.splice(0);
    removePreventUnloadIfNeeded();
}

const preventUnload = event => {
    event.preventDefault();
}

const removePreventUnloadIfNeeded = () => {
    const currentItems = items.filter(item => {
        return item.status === 'pending' || item.status === 'working';
    }).length;

    if (currentItems === 0) {
        window.onbeforeunload = null;
    }
}

const updateItem = (id: number, newValue: Item) => {
    const index = findIndex(items, ['id', id]);

    items[index] = newValue;
}

const startListening = (id: number) => {
    window.Echo.private(`media_downloads.${id}`)
        .subscribed(() => {
            window.onbeforeunload = preventUnload;
        })
        .error(() => {
            updateItem(id, {
                id: id,
                progress: null,
                status: 'failed',
            });

            removePreventUnloadIfNeeded();
        })
        .listen('MediaDownloadPrepare', event => {
            updateItem(id, {
                id: id,
                status: 'working',
                progress: null,
            });
        })
        .listen('MediaDownloadProgressed', event => {
            const index = findIndex(items, ['id', id]);

            if (items[index].status !== 'finished') {
                updateItem(id, {
                    id: id,
                    status: 'working',
                    progress: event.progress,
                });
            }
        })
        .listen('MediaDownloadFinished', event => {
            updateItem(id, {
                id: id,
                progress: 100,
                status: 'finished',
            });

            removePreventUnloadIfNeeded();

            for (let i = 0; i < event.urls.length; i++) {
                setTimeout(() => {
                    const url = event.urls[i];

                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'download-' + i + '.zip';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                }, i * 200);
            }
        })
        .listen('MediaDownloadFailed', event => {
            updateItem(id, {
                id: id,
                progress: null,
                status: 'failed',
            });

            removePreventUnloadIfNeeded();
        });
}
</script>

<style scoped>
.media-download-dialog {
    z-index: 9999;
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    width: 20%;
}
</style>
